import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Container,
    Row,
} from 'react-bootstrap';
import { Link, useLocation, useParams } from 'react-router-dom';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import InstitutionLogo from '@/components/common/InstitutionLogo';
import ConfirmModal from '@/components/common/ConfirmModal';
import ActionNotice, {
    useActionNotice,
} from '@/components/common/ActionNotice';
import { useAuth } from '@/shared/contexts/AuthContext';
import { BUILDING_OWNER_LABELS } from '@/shared/constants/institutions';
import {
    CATEGORY_LABELS,
    CONDITION_LABELS,
    OPEN_STATUSES,
    STATUS_LABELS,
    STATUS_VARIANTS,
} from '@/shared/constants/listings';
import {
    REQUEST_OPEN_STATUSES,
    REQUEST_STATUS_LABELS,
    REQUEST_STATUS_VARIANTS,
} from '@/shared/constants/requests';
import listingService from '@/shared/services/listingService';
import requestService from '@/shared/services/requestService';
import { expiryWords } from '@/shared/utils/listingDates';
import { formatDenverDate } from '@/shared/utils/denverDates';
import { serverMessage } from '@/shared/utils/serverMessage';
import RequestForm from '@/features/requests/components/RequestForm';
import RequestsPanel from '@/features/requests/components/RequestsPanel';
import usePageTitle from '@/shared/hooks/usePageTitle';
import CapitalEquipmentNotice from './components/CapitalEquipmentNotice';
import CompleteExchangeModal from './components/CompleteExchangeModal';
import AskQuestion from './components/AskQuestion';
import './listings.css';

const ACTION_FAILED = 'Could not complete that action. Try again.';

const hasValue = (value) =>
    value !== undefined && value !== null && value !== '';

function Fact({ label, value }) {
    if (!hasValue(value)) {
        return null;
    }
    return (
        <>
            <dt className="col-sm-4">{label}</dt>
            <dd className="col-sm-8">{value}</dd>
        </>
    );
}

/**
 * A labelled group of facts, shown only when at least one fact has a value,
 * so a listing without a value estimate or asset tag shows no empty heading.
 */
function FactGroup({ title, facts }) {
    const shown = facts.filter(([, value]) => hasValue(value));
    if (shown.length === 0) {
        return null;
    }
    return (
        <>
            <h3 className="afe-fact-group">{title}</h3>
            <dl className="row mb-0">
                {shown.map(([label, value]) => (
                    <Fact key={label} label={label} value={value} />
                ))}
            </dl>
        </>
    );
}

function ListingDetailPage() {
    const { id } = useParams();
    // Posting carries a photo-upload failure here rather than discarding a
    // listing that was saved: the item exists, only its photos are missing.
    const photoError = useLocation().state?.photoError;
    const { userProfile } = useAuth();
    const [listing, setListing] = useState(null);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [panelError, setPanelError] = useState('');
    const {
        ref: noticeRef,
        notice,
        error: actionError,
        report,
    } = useActionNotice();
    const [formError, setFormError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [showRequestForm, setShowRequestForm] = useState(false);
    const [requestSent, setRequestSent] = useState(false);
    // One of '', 'withdraw', 'complete', or 'release'; only one confirmation
    // can be open at a time, so a single value carries the whole state.
    const [confirming, setConfirming] = useState('');
    // Named after the item once it is read, so a member holding two of these
    // open can tell the tabs apart.
    usePageTitle(listing?.title || 'Listing');

    // Read as primitives rather than off `userProfile`, so the loader below
    // is not rebuilt every time the context hands back an equal object.
    const viewerId = userProfile?._id;
    const viewerIsAdmin = (userProfile?.roles || []).includes('admin');

    const load = useCallback(async () => {
        const doc = await listingService.get(id);
        setListing(doc);
        // The confirmation describes a request that is live. Once the
        // viewer's standing is anything else, most obviously after they
        // withdraw, it is describing something that no longer exists.
        setRequestSent(
            (sent) =>
                sent &&
                REQUEST_OPEN_STATUSES.includes(doc.viewer?.requestStatus)
        );
        const ownerId = doc.owner?._id || doc.owner;
        const manages =
            (Boolean(viewerId) && String(viewerId) === String(ownerId)) ||
            viewerIsAdmin;
        if (!manages) {
            // A stranger never reads the other requesters' contact details,
            // and the server refuses the route anyway.
            setRequests([]);
            return;
        }
        try {
            const data = await requestService.listForListing(id);
            setRequests(data.requests || []);
            setPanelError('');
        } catch (_error) {
            setPanelError('Could not load the requests on this listing.');
        }
    }, [id, viewerId, viewerIsAdmin]);

    useEffect(() => {
        async function first() {
            setLoading(true);
            setError('');
            try {
                await load();
            } catch (_error) {
                setListing(null);
                setError(
                    'Listing not found. It may have been removed, or the' +
                        ' link may be wrong.'
                );
            } finally {
                setLoading(false);
            }
        }
        first();
    }, [load]);

    // Every transition changes both the listing and its requests, so the page
    // rereads rather than patching one side from the response.
    //
    // Success is reported as loudly as failure. The control that was clicked
    // is usually gone by the time the reread lands, because the panel drops a
    // request row the moment it leaves `open`, and React then drops focus to
    // the body, so nothing on the page says what happened and the next Tab
    // restarts at the skip link.
    const runAction = async (act, done) => {
        setConfirming('');
        try {
            await act();
            report({ notice: done });
        } catch (caught) {
            report({ error: serverMessage(caught, ACTION_FAILED) });
        }
        try {
            // Reread whether or not it worked. A refusal almost always means
            // the other party moved first, so the page that offered the
            // action is already showing a state that no longer exists and
            // retrying from it could never succeed.
            await load();
        } catch (_error) {
            // The listing is unreadable now; the message above still stands.
        }
    };

    const handleCreate = async (body) => {
        setSubmitting(true);
        setFormError('');
        try {
            await requestService.create(id, body);
            setShowRequestForm(false);
            setRequestSent(true);
            await load();
        } catch (caught) {
            setFormError(
                serverMessage(caught, 'Could not send that request. Try again.')
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Container className="py-4">
                <LoadingSpinner message="Loading listing" />
            </Container>
        );
    }
    if (error || !listing) {
        return (
            <Container className="py-4">
                <Alert variant="danger">{error}</Alert>
                <Link to="/listings">Back to browse</Link>
            </Container>
        );
    }

    const owner = listing.owner || {};
    const isOwner = Boolean(userProfile?._id) && userProfile._id === owner._id;
    const isAdmin = viewerIsAdmin;
    const canEdit = isOwner || isAdmin;
    const isOpen = OPEN_STATUSES.includes(listing.status);
    const expiry = expiryWords(listing.expiresAt);
    const categoryLabel = CATEGORY_LABELS[listing.category] || listing.category;
    const photoUrls = listing.photoUrls || [];

    const viewer = listing.viewer || {};
    // A declined or withdrawn request is history: it neither blocks a fresh
    // one nor gives the member anything left to do.
    const hasLiveRequest = REQUEST_OPEN_STATUSES.includes(viewer.requestStatus);

    return (
        <Container className="py-4">
            {photoError && <Alert variant="warning">{photoError}</Alert>}
            <Row className="align-items-start mb-3">
                <Col>
                    <h1 className="h3">{listing.title}</h1>
                    <div>
                        <InstitutionLogo
                            institution={listing.institution}
                            className="me-1"
                        />
                        <Badge bg={STATUS_VARIANTS[listing.status]}>
                            {STATUS_LABELS[listing.status] || listing.status}
                        </Badge>
                    </div>
                </Col>
                <Col xs="auto">
                    {canEdit && (
                        <Button
                            as={Link}
                            to={`/listings/${listing._id}/edit`}
                            variant="outline-primary"
                        >
                            Edit
                        </Button>
                    )}
                </Col>
            </Row>
            <Row className="g-4">
                <Col lg={7}>
                    {/* One large photograph with the rest as thumbnails, so
                        a listing with several photos does not push its
                        description off the screen. */}
                    {photoUrls.length > 0 ? (
                        <div className="afe-detail-photos mb-4">
                            {photoUrls.map((url, index) => (
                                <img
                                    key={url}
                                    src={url}
                                    alt={`Photo ${index + 1} of ${listing.title}`}
                                    className={
                                        index === 0
                                            ? 'afe-detail-photo-main'
                                            : 'afe-detail-photo-thumb'
                                    }
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="afe-detail-placeholder mb-4">
                            {categoryLabel}
                        </div>
                    )}
                    <Card className="mb-4">
                        <Card.Body>
                            <h2 className="h5">Description</h2>
                            <p className="mb-0 afe-detail-description">
                                {listing.description}
                            </p>
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={5}>
                    {listing.isCapitalEquipment && (
                        <CapitalEquipmentNotice
                            institution={listing.institution}
                        />
                    )}
                    <Card className="mb-3">
                        <Card.Body>
                            <h2 className="h5">Details</h2>
                            <FactGroup
                                title="The item"
                                facts={[
                                    ['Category', categoryLabel],
                                    [
                                        'Condition',
                                        CONDITION_LABELS[listing.condition] ||
                                            listing.condition,
                                    ],
                                    ['Quantity', listing.quantity],
                                    ['Dimensions', listing.dimensions],
                                ]}
                            />
                            <FactGroup
                                title="Where it is"
                                facts={[
                                    ['Building', listing.building],
                                    ['Room', listing.room],
                                    [
                                        'Serviced by',
                                        BUILDING_OWNER_LABELS[
                                            listing.buildingOwner
                                        ],
                                    ],
                                    ['Pickup notes', listing.pickupNotes],
                                ]}
                            />
                            <FactGroup
                                title="Value and tags"
                                facts={[
                                    ['Asset tag', listing.assetTag],
                                    [
                                        'Estimated value',
                                        listing.estimatedValueUsd === undefined
                                            ? ''
                                            : `$${listing.estimatedValueUsd.toLocaleString()}`,
                                    ],
                                    [
                                        'Estimated weight',
                                        listing.estimatedWeightLb === undefined
                                            ? ''
                                            : `${listing.estimatedWeightLb} lb`,
                                    ],
                                ]}
                            />
                            {/* The deadline belongs to an item still on offer.
                                A closed one carries a stored expiry that means
                                nothing to the reader, and its status badge
                                already says where it stands. */}
                            <FactGroup
                                title="Timing"
                                facts={[
                                    [
                                        'Pick up by',
                                        listing.pickupBy ? (
                                            <time dateTime={listing.pickupBy}>
                                                {formatDenverDate(
                                                    listing.pickupBy
                                                )}
                                            </time>
                                        ) : (
                                            ''
                                        ),
                                    ],
                                    [
                                        'Expires',
                                        isOpen && expiry ? (
                                            <time dateTime={listing.expiresAt}>
                                                {expiry}
                                            </time>
                                        ) : (
                                            ''
                                        ),
                                    ],
                                ]}
                            />
                        </Card.Body>
                    </Card>
                    <Card className="mb-3">
                        <Card.Body>
                            <h2 className="h5">Posted by</h2>
                            <p className="mb-1">{owner.name}</p>
                            {owner.department && (
                                <p className="text-muted mb-1">
                                    {owner.department}
                                </p>
                            )}
                            {owner.email && (
                                <p className="mb-1">
                                    <a href={`mailto:${owner.email}`}>
                                        {owner.email}
                                    </a>
                                </p>
                            )}
                            {owner.phone && (
                                <p className="mb-3">{owner.phone}</p>
                            )}
                        </Card.Body>
                    </Card>
                    {/* The actions stack at one width, so the primary and
                        secondary choices line up instead of sizing to their
                        labels. */}
                    <div className="afe-detail-actions">
                        <ActionNotice
                            ref={noticeRef}
                            notice={notice}
                            error={actionError}
                        />
                        {requestSent && (
                            <Alert variant="success">
                                Request sent. The poster will be notified.
                            </Alert>
                        )}
                        {/* The owner is never offered their own item; the server
                            refuses it with OWN_LISTING either way. */}
                        {!isOwner && hasLiveRequest && (
                            <section aria-label="Your request" className="mb-3">
                                <h2 className="h5">Your request</h2>
                                <p className="mb-2">
                                    <Badge
                                        bg={
                                            REQUEST_STATUS_VARIANTS[
                                                viewer.requestStatus
                                            ]
                                        }
                                    >
                                        {
                                            REQUEST_STATUS_LABELS[
                                                viewer.requestStatus
                                            ]
                                        }
                                    </Badge>
                                </p>
                                <div className="d-flex gap-2">
                                    <Link
                                        to={`/requests/${viewer.requestId}`}
                                        className="btn btn-outline-primary btn-sm"
                                    >
                                        View request
                                    </Link>
                                    {viewer.requestStatus === 'open' && (
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() =>
                                                setConfirming('withdraw')
                                            }
                                        >
                                            Withdraw request
                                        </Button>
                                    )}
                                </div>
                            </section>
                        )}
                        {!isOwner && !hasLiveRequest && isOpen && (
                            <Button
                                className="w-100"
                                onClick={() => setShowRequestForm(true)}
                            >
                                Request this item
                            </Button>
                        )}
                        {!isOwner && isOpen && Boolean(viewerId) && (
                            <AskQuestion listingId={listing._id} />
                        )}
                    </div>
                </Col>
            </Row>
            {canEdit && (
                <RequestsPanel
                    requests={requests}
                    listing={listing}
                    error={panelError}
                    onAccept={(requestId) =>
                        runAction(
                            () => requestService.accept(requestId),
                            'Request accepted. The item is claimed and every' +
                                ' other request on it is declined.'
                        )
                    }
                    onDecline={(requestId) =>
                        runAction(
                            () => requestService.decline(requestId),
                            'Request declined.'
                        )
                    }
                    onComplete={() => setConfirming('complete')}
                    onRelease={() => setConfirming('release')}
                />
            )}
            <RequestForm
                show={showRequestForm}
                listingTitle={listing.title}
                submitting={submitting}
                error={formError}
                onCancel={() => setShowRequestForm(false)}
                onSubmit={handleCreate}
            />
            <CompleteExchangeModal
                show={confirming === 'complete'}
                listingTitle={listing.title}
                onCancel={() => setConfirming('')}
                onConfirm={(outcome) =>
                    runAction(
                        () => listingService.complete(id, outcome),
                        'The exchange is closed out.'
                    )
                }
            />
            <ConfirmModal
                show={confirming === 'withdraw'}
                title="Withdraw this request?"
                confirmLabel="Withdraw it"
                cancelLabel="Keep it"
                onCancel={() => setConfirming('')}
                onConfirm={() =>
                    runAction(
                        () => requestService.withdraw(viewer.requestId),
                        'Your request is withdrawn.'
                    )
                }
            >
                <p className="mb-0">
                    The poster stops seeing it and you can ask again later while
                    the item is still open.
                </p>
            </ConfirmModal>
            <ConfirmModal
                show={confirming === 'release'}
                title="Release the claim?"
                confirmLabel="Release the claim"
                cancelLabel="Keep the claim"
                onCancel={() => setConfirming('')}
                onConfirm={() =>
                    runAction(
                        () => listingService.release(id),
                        'The claim is released and the item is back on offer.'
                    )
                }
            >
                <p className="mb-0">
                    {listing.title} goes back on offer and the accepted request
                    is declined.
                </p>
            </ConfirmModal>
        </Container>
    );
}

export default ListingDetailPage;
